import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import { CartItem, PaymentOption } from 'src/models/cart/_types';
import { Coupon } from 'src/models/coupon/_types';
import { ProductVariant } from 'src/models/product/_types';
import { fetchAllTrainers } from 'src/models/trainer/trainer';
import * as Yup from 'yup';

interface UseItemSettingsOptions {
  /** User for the item settings */
  user?: any;
}

export const useItemSettings = (
  item: CartItem,
  onSubmit: (updatedItem: CartItem) => void,
  options?: UseItemSettingsOptions
) => {
  const user = options?.user;

  const { data: trainers } = useQuery({
    queryKey: ['trainers', 'all'],
    queryFn: fetchAllTrainers,
    staleTime: 60000,
    gcTime: 60000,
  });

  const { user: currentUser, session } = useSession();
  const requireChargedBy = session?.requireChargedBy || false;

  const [showCreateCouponForm, setShowCreateCouponForm] =
    useState<boolean>(false);
  const [couponType, setCouponType] = useState<'subscription' | 'purchase'>(
    'purchase'
  );

  const formik = useFormik({
    initialValues: {
      paymentOption: item.paymentOption,
      quantity: item.quantity || 1,
      startOn: item.startOn,
      coupon: item.coupon,
      primaryCommissioneeIds: item.primaryCommissioneeIds,
      secondaryCommissioneeIds: item.secondaryCommissioneeIds,
      chargedById: item.chargedById,
      cancelDate: item.cancelDate,
      billingCycleAnchor: item.billingCycleAnchor,
      overrideProrationBehavior: item.overrideProrationBehavior,
      backdateIsStartdate: item.backdateIsStartdate,
      activationDate: item.activationDate,
      expiresOn: item.expiresOn,
      shareWithAllDependents: item.shareWithAllDependents || false,
      users: item.users || [],
    },
    validationSchema: Yup.object({
      paymentOption: Yup.object()
        .shape({
          amount: Yup.number()
            .integer()
            .min(0, 'Amount must a positive number')
            .required('Please enter a valid amount'),
        })
        .required('Please select a payment option'),
      quantity: Yup.number()
        .integer()
        .min(1, 'Quantity must be at least 1')
        .required('Please enter a quantity'),
      coupon: Yup.object().nullable(),
      chargedById: requireChargedBy
        ? Yup.number().integer().required('Charged by is required')
        : Yup.number().integer().nullable(),
      primaryCommissioneeIds: Yup.array()
        .of(Yup.number().integer())
        .test(
          'primaryCommissioneeIds',
          'This commissionee does not have commission configured for this item',
          (value) => {
            if (!value) return true;

            // For ProductVariant, validate against the parent Product
            const purchaseId =
              item.itemType === 'ProductVariant'
                ? (item.item as unknown as ProductVariant)?.productId
                : item.itemId;
            const purchaseType =
              item.itemType === 'ProductVariant' ? 'Exercisecom::Product' : item.itemType;

            const invalidTrainers = value.filter((id) => {
              const stringId = id?.toString();

              if (!stringId) return true;
              const trainer = trainers?.find((trainer) =>
                trainer.trainerId.toString().includes(stringId)
              );

              if (
                trainer &&
                !trainer.commission &&
                !trainer.landingItems?.find(
                  (li) =>
                    li.commission &&
                    li.purchaseId == purchaseId &&
                    li.purchaseType == purchaseType
                )
              ) {
                return trainer;
              } else {
                return null;
              }
            });

            return invalidTrainers.length == 0;
          }
        ),
      secondaryCommissioneeIds: Yup.array()
        .of(Yup.number().integer())
        .test(
          'secondaryCommissioneeIds',
          'This commissionee does not have secondary commission configured for this item',
          (value) => {
            if (!value) return true;

            // For ProductVariant, validate against the parent Product
            const purchaseId =
              item.itemType === 'ProductVariant'
                ? (item.item as unknown as ProductVariant)?.productId
                : item.itemId;
            const purchaseType =
              item.itemType === 'ProductVariant' ? 'Exercisecom::Product' : item.itemType;

            const invalidTrainers = value.filter((id) => {
              const stringId = id?.toString();

              if (!stringId) return true;
              const trainer = trainers?.find((trainer) =>
                trainer.trainerId.toString().includes(stringId)
              );

              if (
                trainer &&
                !trainer.secondaryCommission &&
                !trainer.landingItems?.find(
                  (li) =>
                    li.secondaryCommission &&
                    li.purchaseId == purchaseId &&
                    li.purchaseType == purchaseType
                )
              ) {
                return trainer;
              } else {
                return null;
              }
            });

            return invalidTrainers.length == 0;
          }
        ),
      cancelDate: Yup.date().nullable(),
      startOn: Yup.date().nullable(),
      billingCycleAnchor: Yup.object().nullable(),
      overrideProrationBehavior: Yup.object().nullable(),
      backdateIsStartdate: Yup.boolean(),
      activationDate: Yup.date().nullable(),
      expiresOn: Yup.date().nullable(),
      shareWithAllDependents: Yup.boolean(),
      users: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.number().integer(),
            name: Yup.string(),
          })
        )
        .nullable(),
    }),
    onSubmit: (values) => {
      const updatedItem = { ...item, ...values };
      onSubmit(updatedItem);
    },
    validateOnMount: true,
  });

  const isSubscription = (value: PaymentOption | undefined) =>
    value?.type == 'subscription';
  const yearly = (value: PaymentOption | undefined) =>
    value?.interval == 'year';
  const weekly = (value: PaymentOption | undefined) =>
    value?.interval == 'week';

  const handleCreateCoupon = async (newCoupon: Partial<Coupon>) => {
    setShowCreateCouponForm(false);
    formik.setFieldValue('coupon', newCoupon);
  };

  return {
    formik,
    trainers,
    showCreateCouponForm,
    setShowCreateCouponForm,
    couponType,
    setCouponType,
    handleCreateCoupon,
    isSubscription,
    yearly,
    weekly,
    user,
    currentUser,
  };
};
